import { onMounted, onBeforeUnmount } from 'vue';
import * as signalR from '@microsoft/signalr';
import { useAuth } from '@/stores/auth';
import { useIssuesStore } from '@/stores/issues';
import { useDashboardSummary } from './useDashboardSummary';
/**
 * Subscribes to the DashboardHub for `issueChanged` events.
 *
 * Mounted at AppLayout.vue level so it survives list ↔ detail navigation.
 * On `issueChanged`: calls issuesStore.handleIssueChanged (re-fetches single item,
 * patches it in place — no full list reload).
 * On `summaryInvalidated`: triggers dashboard summary refresh (pre-existing behavior).
 *
 * Reuses the same hub URL as useSignalRDashboard (/hubs/dashboard) — there is only
 * one hub connection per layout mount; the two composables build separate connections
 * but share the same hub endpoint. A future refactor could deduplicate via a shared
 * connection singleton, but parallel connections to the same hub are safe in SignalR.
 */
export function useIssuesSignalR() {
    const auth = useAuth();
    const store = useIssuesStore();
    const { refresh: refreshDashboard } = useDashboardSummary();
    let connection = null;
    onMounted(() => {
        connection = new signalR.HubConnectionBuilder()
            .withUrl('/hubs/dashboard', {
            accessTokenFactory: () => auth.getAccessToken().then((t) => t ?? ''),
        })
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Warning)
            .build();
        connection.on('issueChanged', (payload) => {
            void store.handleIssueChanged(payload.issueId, payload.changeKind);
            // Also invalidate dashboard summary so the stats update.
            void refreshDashboard();
        });
        connection.on('summaryInvalidated', () => {
            void refreshDashboard();
        });
        connection.start().catch((err) => {
            // Non-fatal — hub may not be deployed yet.
            console.warn('[useIssuesSignalR] connection failed', err);
        });
    });
    onBeforeUnmount(() => {
        if (connection) {
            void connection.stop();
            connection = null;
        }
    });
}
